/**
 * The signed-in console: the shell, filled in with this organization and this
 * person, wrapped around whatever screen the router resolved.
 *
 * ## Why this exists as its own component rather than inside `routes.tsx`
 *
 * `AppShell`, `Sidebar` and `Topbar` know nothing about Namu. They take
 * navigation, a breadcrumb and a user as props and are testable without a
 * store, a router or an API client, which is why the layout group has no
 * imports outside itself. Something has to hold the other end: read who is
 * signed in, filter the navigation by what their role holds, turn an address
 * into a trail. That is this file, and keeping it out of the layout group is
 * what stops a shell component from quietly growing a dependency on the auth
 * store.
 *
 * `routes.tsx` is the other candidate and is worse. It is the file somebody
 * edits every time a screen is added, and a component defined in it gives up
 * fast refresh for the whole route table.
 *
 * ## The provider order is load-bearing
 *
 * `ToastProvider` is outside `AppShell` rather than inside it. A toast raised
 * while a modal is open has to render above the modal, and a provider mounted
 * inside the shell puts its viewport inside the shell's stacking context, where
 * `--z-modal` is above it whatever z-index the toast asks for. Outside, both are
 * children of the same root and the token order decides, which is what the
 * token order is for.
 *
 * ## Every entry the sidebar draws is one this role can follow
 *
 * The filter is a usability decision and never a security one: the server
 * checks the same table on every request and is the only thing enforcing
 * anything. What it removes is the experience of clicking a link that refuses
 * you, which leaves a person unable to tell whether the refusal is the bug or
 * the link is.
 */

import { Suspense } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import type { ComponentType } from 'react'
import {
  BroadcastsIcon,
  CasesIcon,
  ClockIcon,
  ConversationsIcon,
  DashboardIcon,
  InsightsIcon,
  ProjectsIcon,
  ReportsIcon,
  SettingsIcon,
  TasksIcon,
  TeamIcon,
} from '@/components/icons/Icons'
import type { IconProps } from '@/components/icons/Icons'
import { AppShell, Sidebar, Topbar } from '@/components/layout'
import type { NavGroup, SidebarOrganization } from '@/components/layout'
import { ToastProvider } from '@/components/overlays'
import { buttonShape } from '@/components/primitives'
import { useAuthStore } from '@/store/authStore'
import { can } from '@/store/permissions'
import { ScreenError, ScreenErrorBoundary } from '../ScreenError/ScreenError'
import { ScreenLoading } from '../ScreenLoading/ScreenLoading'
import { breadcrumbFor } from '../breadcrumbs'
import { NAVIGATION } from '../navigation'
import type { NavIconName } from '../navigation'
import styles from './ConsoleFrame.module.css'

/**
 * The icon set, resolved by name.
 *
 * `navigation.ts` names its icons in strings rather than importing components,
 * so the table stays a plain data file that a test can read without rendering
 * anything. The record is typed by `NavIconName`, so adding an entry to the
 * table without an icon here is a compile error rather than a hole in the
 * sidebar.
 */
const NAV_ICONS: Record<NavIconName, ComponentType<IconProps>> = {
  dashboard: DashboardIcon,
  projects: ProjectsIcon,
  conversations: ConversationsIcon,
  insights: InsightsIcon,
  cases: CasesIcon,
  broadcasts: BroadcastsIcon,
  tasks: TasksIcon,
  reports: ReportsIcon,
  team: TeamIcon,
  /*
   * The clock, because the audit log is a history and the icon set has no
   * separate mark for one. Drawing a fourteenth glyph that means "audit" and
   * nothing else would add a shape to the family for a single nav row, and the
   * icon file's own rule is that a mark earns its place by being reused.
   */
  audit: ClockIcon,
  settings: SettingsIcon,
}

/**
 * The six roles as the words a person would use for themselves.
 *
 * `field_agent` with the underscore showing is the string the database holds,
 * and printing it in the account menu tells somebody they are looking at a
 * column name.
 */
const ROLE_LABELS: Readonly<Record<string, string>> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  analyst: 'Analyst',
  field_agent: 'Field agent',
  viewer: 'Viewer',
}

export function ConsoleFrame() {
  const location = useLocation()
  const user = useAuthStore((state) => state.user)
  const organization = useAuthStore((state) => state.organization)
  const signOut = useAuthStore((state) => state.signOut)

  const role = user?.role ?? null

  const groups: NavGroup[] = NAVIGATION.map((section) => ({
    label: section.label,
    items: section.entries
      .filter((entry) => entry.permission === null || can(role, entry.permission))
      .map((entry) => {
        const Icon = NAV_ICONS[entry.icon]
        return { to: entry.to, label: entry.label, icon: <Icon size={18} />, end: entry.end }
      }),
    // A section whose every entry was filtered out would otherwise render as a
    // heading with nothing under it, which is how a viewer's sidebar ends in
    // the word "Organization" and a gap.
  })).filter((group) => group.items.length > 0)

  /*
   * The organization list has exactly one entry, and that is a statement about
   * the platform rather than a stub. A staff session belongs to one
   * organization: `StaffSession` carries one, the token is issued against one,
   * and there is no endpoint that lists others. `Sidebar` renders a plain name
   * rather than a switcher when there is one of them, which is the right
   * behaviour and the reason it was built that way.
   */
  const organizations: SidebarOrganization[] = organization
    ? [
        {
          id: organization.id,
          name: organization.name,
          role: role ? ROLE_LABELS[role] : undefined,
        },
      ]
    : []

  return (
    <ToastProvider>
      <AppShell
        sidebar={
          <Sidebar
            groups={groups}
            organizations={organizations}
            currentOrgId={organization?.id ?? ''}
            /*
             * There is nowhere to switch to, so this is not wired to anything.
             * It is required by the props rather than optional because the day
             * an organization list exists, a sidebar that silently ignored the
             * choice would be the bug; a no-op here is visible in one file.
             */
            onSelectOrganization={() => undefined}
          />
        }
        topbar={
          <Topbar
            breadcrumb={breadcrumbFor(location.pathname)}
            user={{
              name: user?.name ?? 'Signed in',
              email: user?.email ?? '',
              role: role ? ROLE_LABELS[role] : undefined,
            }}
            onSignOut={() => void signOut()}
          />
        }
      >
        {/*
          Both boundaries are inside the shell, and that placement is the whole
          reason this component exists.

          Around the shell, a first visit to a route unmounts the sidebar and
          the top bar and brings them back, which reads as a full page reload on
          the slowest connections, which are the ones this product runs on. And
          a screen that throws would take the navigation with it, leaving an
          empty document with no way out except retyping an address.

          The error boundary is keyed on the address, because React error
          boundaries latch: once caught, the fallback stays until state changes,
          so without the key somebody who navigates away from the broken screen
          carries the message with them onto every screen after it. The Suspense
          boundary does not need the key. It resolves on its own, and keying it
          would throw away a screen's state on every navigation back to it.
        */}
        <div className={styles.content}>
          <ScreenErrorBoundary
            key={location.pathname}
            fallback={
              <ScreenError
                action={
                  <Link className={buttonShape()} to="/dashboard">
                    Back to Today
                  </Link>
                }
              />
            }
          >
            <Suspense fallback={<ScreenLoading />}>
              <Outlet />
            </Suspense>
          </ScreenErrorBoundary>
        </div>
      </AppShell>
    </ToastProvider>
  )
}
